import { createEffect, createEvent, createStore, type EffectParams, restore, sample } from 'effector'
import { z } from 'zod'
import { zfd } from 'zod-form-data'

import { getRegisterInfo } from '@portal/client-info'
import type { PortalAPIError } from '@portal/portal-api'
import type { ColorToken } from '@portal/styled-system/tokens'

import { portalProfileAPI } from '@/entities/profile/portal'
import { attachEmailConfirmation } from '@/features/email-confirm'
import { getPortalAPIErrorMessage } from '@/shared/api/portal/error-message'
import { createFormValidation } from '@/shared/lib/factory'
import { $i18n, $lang } from '@/shared/lib/i18n'
import { createHideFx } from '@/shared/ui/modal'
import { type NotificationArgs, showNotificationFx } from '@/shared/ui/notification-dialog'

export const formSubmitted = createEvent<FormData>()

export const laterButtonClicked = createEvent()

export const showFx = createEffect<
    {
        title?: string
        titleIconClass?: string
        intro?: string
        headerBackgroundColor?: ColorToken
    },
    string
>()
export const hideFx = createHideFx(showFx)

// ------------- Form validation

const EmailPairFormValuesSchema = zfd.formData({
    email: zfd.text(z.email()),
})

const { formValidationSucceeded, formValidationFailed } = createFormValidation({
    formSubmitted,
    validationSchema: EmailPairFormValuesSchema,
    mapError: (i18n, _error) => ({ message: i18n.TEXT_ERROR_EMAIL_INVALID }),
})

export const $formErrorMessage = createStore('')
    .on(formValidationFailed, (_, { message }) => message)
    .reset(formSubmitted, hideFx)

export const $lastFormValues = restore(formValidationSucceeded, { email: '' }).reset(hideFx)

// ------------- Email pairing

const pairEmailAddFailed = createEvent<{ error: PortalAPIError }>()
const verifyLinkSent = createEvent<{ email: string }>()

const pairEmailAddFx = attachEmailConfirmation({
    effect: portalProfileAPI.pairEmailAdd,
    onError: pairEmailAddFailed,
    onSuccess: verifyLinkSent,
})

sample({
    clock: verifyLinkSent,
    fn: ({ email }): NotificationArgs => ({ notification: 'email_link_sent', email }),
    target: showNotificationFx,
})

export const $submitFormButtonDisabled = pairEmailAddFx.pending

// Custom error message for "email already exists" error
sample({
    clock: pairEmailAddFailed,
    source: $i18n,
    fn: ({ i18n }, { error }): string => {
        const code = error.code
        switch (code) {
            case 'email_account_already_exist':
            case 'platform_account_already_exist':
            case 'account_already_exist':
                return i18n.EMAIL_CHANGE_ERROR__OCCUPIED
        }

        return getPortalAPIErrorMessage(error, i18n)
    },
    target: $formErrorMessage,
})

sample({
    clock: formValidationSucceeded,
    source: $lang,
    fn: (lang, data): EffectParams<typeof pairEmailAddFx> => ({
        ...data,
        lang,
        register_info: getRegisterInfo(lang),
    }),
    target: pairEmailAddFx,
})

sample({
    clock: pairEmailAddFx.done,
    target: hideFx,
})

sample({
    clock: laterButtonClicked,
    target: hideFx,
})
